import { Button } from "@/Components/ui/button";
import { Card } from "@/Components/ui/card";
import {
   Select,
   SelectContent,
   SelectItem,
   SelectTrigger,
   SelectValue,
} from "@/Components/ui/select";
import { Switch } from "@/Components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { useAdminStore } from "@/store/useAdminStore";
import { useState } from "react";
import { HiCheck, HiOutlineFolderAdd, HiOutlineX } from "react-icons/hi";

interface Item {
   id: number;
   name: string;
   [key: string]: any; // if you have more fields
}

interface NodeData {
   nodeId: number;
   previousNode?: number;
   nextNode?: number;
   items?: Item[];
   [key: string]: any;
}

interface Node {
   data: NodeData;
   [key: string]: any;
}

interface ContextMenuProps {
   top?: number;
   left?: number;
   right?: number;
   bottom?: number;
   selectedNodes: Node[];
   onClose: () => void;
   optionPairId: number;
   onRefresh: () => Promise<void>;
}

const ContextMenu: React.FC<ContextMenuProps> = ({
   top,
   left,
   right,
   bottom,
   selectedNodes,
   onClose,
   optionPairId,
   onRefresh,
}) => {
   const { handleBranch, handleDelete, handleAddOption, allOptions } =
      useAdminStore();
   const { toast } = useToast();

   // The menu can be in one of these states:
   const [menuState, setMenuState] = useState<
      "main" | "branch" | "connect" | "addOptions"
   >("main");

   // For branch usage (or any action that uses items):
   const [selectedBranchItems, setSelectedBranchItems] = useState<number[]>([]);

   const [addToEach, setAddToEach] = useState(true);
   const [selectedOptionId, setSelectedOptionId] = useState<null | number>(
      null
   );
   // ------------------------------------
   // Universal Handlers for each action:
   // ------------------------------------
   const handleAction = async (action: "branch" | "addOptions") => {
      try {
         if (action === "branch") {
            if (
               selectedNodes.length !== 1 ||
               !selectedNodes[0].data.previousNode
            ) {
               toast({
                  title: "Branch Error",
                  description:
                     "Select exactly one node with a previousNode to branch from.",
                  variant: "destructive",
               });
               return;
            }

            await handleBranch(
               selectedNodes[0].data.previousNode,
               // For demonstration, we take the first item's optionId — adapt as needed
               selectedNodes[0].data.items?.[0]?.optionId,
               selectedBranchItems,
               optionPairId
            );

            await onRefresh();

            toast({
               title: "Branch Created",
               description: `Successfully branched from node ${selectedNodes[0].data.nodeId}.`,
            });
         }

         if (action === "addOptions") {
            if (!selectedOptionId) {
               toast({
                  title: "Add Options Error",
                  description: "Please select at least one option.",
                  variant: "destructive",
               });
               return;
            }

            // We pass the selected items and the boolean (addToEach)
            // You can adapt handleAddOption to handle your specific logic:
            // e.g. "Add to each node" vs "Connect" the items, etc.
            await handleAddOption(
               selectedNodes.map((node) => node.data.nodeId),
               selectedOptionId,
               optionPairId,
               addToEach
            );

            await onRefresh();

            toast({
               title: "Items Added",
               description: `Successfully ${
                  addToEach ? "added" : "connected"
               }  items.`,
            });
         }

         // If successful, close the context menu
         onClose();
      } catch (error) {
         toast({
            title: "Error",
            description: "Action failed. Please try again.",
            variant: "destructive",
         });
      }
   };

   return (
      <Card
         className="absolute z-50 p-4 shadow-lg bg-white border border-gray-200 rounded-lg w-72"
         style={{
            top: top ?? undefined,
            left: left ?? undefined,
            right: right ?? undefined,
            bottom: bottom ?? undefined,
         }}
         onClick={(e) => e.stopPropagation()} // Prevent menu from closing when clicking inside
      >
         {/* Header */}
         <div className="flex justify-between items-center mb-2">
            <p className="text-sm font-semibold">
               {menuState === "main"
                  ? `Selected Nodes: ${selectedNodes.length}`
                  : "Back"}
            </p>
            <Button
               variant="ghost"
               size="icon"
               onClick={
                  menuState === "main" ? onClose : () => setMenuState("main")
               }
            >
               <HiOutlineX size={20} />
            </Button>
         </div>

         {/* ---------------- MAIN MENU ---------------- */}
         {menuState === "main" && (
            <div className="space-y-2">
               <Button
                  onClick={() => setMenuState("branch")}
                  className="w-full"
               >
                  <HiOutlineFolderAdd className="mr-2" size={18} />
                  Branch Selected
               </Button>

               <Button
                  onClick={() => setMenuState("addOptions")}
                  className="w-full"
               >
                  Add Options
               </Button>

               <Button
                  onClick={async () => {
                     const nodeIds = selectedNodes.map((node) =>
                        node.id.toString()
                     );

                     // Optimistically update UI
                     const nodesToRemove = new Set(nodeIds);
                     const remainingNodes = rfNodes.filter(
                        (node) => !nodesToRemove.has(node.id)
                     );
                     const remainingEdges = rfEdges.filter(
                        (edge) =>
                           !nodesToRemove.has(edge.source) &&
                           !nodesToRemove.has(edge.target)
                     );

                     setRfNodes(remainingNodes);
                     setRfEdges(remainingEdges);

                     // Close menu immediately for better UX
                     onClose();

                     try {
                        // Perform the actual delete
                        await handleDelete(nodeIds);
                     } catch (error) {
                        // If it fails, restore the previous state
                        await onRefresh();
                        toast({
                           title: "Error",
                           description: "Failed to delete nodes",
                           variant: "destructive",
                        });
                     }
                  }}
                  variant="destructive"
                  className="w-full"
               >
                  <HiOutlineX className="mr-2" size={18} />
                  Delete Selected
               </Button>
            </div>
         )}

         {/* -------------- BRANCH MENU -------------- */}
         {menuState === "branch" && (
            <div className="space-y-2">
               <p className="text-sm text-gray-600">
                  Select the items to branch from:
               </p>

               <div className="space-y-1 max-h-40 overflow-auto border p-2 rounded-md bg-gray-50">
                  {selectedNodes.length > 0 &&
                     selectedNodes[0].data.items?.map((item) => {
                        const isSelected = selectedBranchItems.includes(
                           item.id
                        );
                        return (
                           <Button
                              key={item.id}
                              variant="ghost"
                              className="flex justify-between w-full"
                              onClick={() =>
                                 setSelectedBranchItems((prev) => {
                                    const newSet = new Set(prev);
                                    isSelected
                                       ? newSet.delete(item.id)
                                       : newSet.add(item.id);
                                    return Array.from(newSet);
                                 })
                              }
                           >
                              {item.name}
                              {isSelected && <HiCheck size={16} />}
                           </Button>
                        );
                     })}
               </div>

               <Button
                  onClick={() => handleAction("branch")}
                  className="w-full mt-2"
               >
                  Confirm Branch
               </Button>
            </div>
         )}

         {/* -------------- CONNECT MENU -------------- */}
         {menuState === "connect" && (
            <div className="space-y-2">
               <p className="text-sm text-gray-600">
                  This will connect the selected nodes.
               </p>
               <Button
                  onClick={() => {
                     // Replace with appropriate action - "connect" is not a valid action type
                     // You might want to implement a proper connect handler here
                     // For now, I'll use "addOptions" which is a valid action type
                     handleAction("addOptions");
                  }}
                  className="w-full"
               >
                  Confirm Connect
               </Button>
            </div>
         )}

         {/* -------------- ADD ITEMS MENU (Multi-Select + Switch) -------------- */}
         {menuState === "addOptions" && (
            <div className="space-y-2">
               <p className="text-sm text-gray-600">
                  Select items to add or connect:
               </p>

               <Select
                  onValueChange={(value) => setSelectedOptionId(Number(value))}
               >
                  <SelectTrigger className="w-full">
                     <SelectValue placeholder="Select an option" />
                  </SelectTrigger>
                  <SelectContent>
                     {allOptions.map(
                        (option) =>
                           option.id && (
                              <SelectItem
                                 key={option.id}
                                 value={option.id.toString()}
                              >
                                 {option.name}
                              </SelectItem>
                           )
                     )}
                  </SelectContent>
               </Select>

               {/* Toggle between "Add to Each" or "Connect" */}
               <div className="flex items-center justify-between mt-2">
                  <span className="text-sm">Connect</span>
                  <Switch checked={addToEach} onCheckedChange={setAddToEach} />
                  <span className="text-sm">Add Options</span>
               </div>

               <Button
                  onClick={() => handleAction("addOptions")}
                  className="w-full mt-2"
               >
                  Confirm
               </Button>
            </div>
         )}
      </Card>
   );
};

export default ContextMenu;
